import styled from 'styled-components';

export const LoginContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background: linear-gradient(
    to bottom right,
    ${({ theme }) => theme.landing.gradientFrom},
    ${({ theme }) => theme.landing.gradientVia},
    ${({ theme }) => theme.landing.gradientTo}
  );
`;

export const LoginCard = styled.div`
  width: 100%;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid rgba(203, 196, 210, 0.3);
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  box-shadow: 0px 20px 40px -10px ${({ theme }) => theme.colors.shadow};
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 440px;

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing.lg};
  }
`;

export const ForgotPasswordCard = styled.div`
  width: 100%;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid rgba(203, 196, 210, 0.3);
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  box-shadow: 0px 20px 40px -10px ${({ theme }) => theme.colors.shadow};
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 500px;

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing.lg};
  }
`;

export const BrandAnchor = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding-bottom: ${({ theme }) => theme.spacing.lg};
`;

export const Heading = styled.h1`
  font-weight: 600;
  font-size: 1.875rem;
  line-height: 1.2;
  letter-spacing: -0.025em;
  color: ${({ theme }) => theme.landing.text};
  margin: 0;
  padding-top: 12px;

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    font-size: 1.5rem;
  }
`;

export const FormSection = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  padding-bottom: ${({ theme }) => theme.spacing.md};
`;

export const PasswordHeader = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`;

export const PasswordLabel = styled.label`
  font-weight: 400;
  font-size: 1rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.landing.text};

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    font-size: 0.95rem;
  }
`;

export const ForgotPasswordLink = styled.a`
  font-weight: 400;
  font-size: 1rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.landing.text};
  cursor: pointer;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    font-size: 0.95rem;
  }
`;

export const PasswordInputWrapper = styled.div`
  width: 100%;
  margin-top: 4px;
  position: relative;
`;

export const SignInButton = styled.button`
  width: 100%;
  background: ${({ theme }) => theme.landing.buttonBg};
  color: #fff;
  border: none;
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};

  font-weight: 700;
  font-size: 1rem;
  line-height: 1.5;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    opacity: 0.9;
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    font-size: 0.95rem;
  }
`;

export const SignUpText = styled.p`
  font-weight: 300;
  font-size: 1rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.landing.text};
  margin: 0;
  padding-top: ${({ theme }) => theme.spacing.xl};
  text-align: center;

  span {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.primary};
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    font-size: 0.95rem;
  }
`;

export const PasswordToggleButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.landing.text};
  opacity: 0.6;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.landing.buttonBg};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

export const SignUpContainer = styled(LoginContainer)`
  box-sizing: border-box;

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    height: auto;
    min-height: 100vh;
    min-height: 100dvh;
    align-items: flex-start;
    padding: 72px 12px 24px;
  }
`;

export const SignUpCard = styled(LoginCard)`
  max-width: 560px;
  padding: ${({ theme }) => theme.spacing.lg};

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    margin: auto;
    padding: ${({ theme }) => theme.spacing.md};
  }
`;

export const SignUpBrandAnchor = styled(BrandAnchor)`
  padding-bottom: ${({ theme }) => theme.spacing.md};

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    padding-bottom: ${({ theme }) => theme.spacing.sm};

    h1 {
      padding-top: 0;
    }
  }
`;

export const SignUpForm = styled(FormSection)`
  padding-bottom: 0;

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    gap: ${({ theme }) => theme.spacing.sm};
  }
`;

export const SignUpFooterText = styled(SignUpText)`
  padding-top: ${({ theme }) => theme.spacing.md};

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    padding-top: ${({ theme }) => theme.spacing.sm};
  }
`;

export const FieldRow = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${({ theme }) => theme.spacing.sm};

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const RequiredMark = styled.span`
  color: ${({ theme }) => theme.landing.errorColor || '#e53e3e'};
  margin-left: 3px;
  font-weight: 600;
`;

export const PolicyChips = styled.ul`
  width: 100%;
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
`;

export const PolicyChip = styled.li`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  line-height: 1.4;
  white-space: nowrap;
  color: ${({ theme, $passed }) =>
    $passed ? theme.landing.successColor || '#16a34a' : theme.landing.text};
  opacity: ${({ $passed }) => ($passed ? 1 : 0.65)};
  transition:
    color 0.15s ease,
    opacity 0.15s ease;

  svg {
    flex-shrink: 0;
  }
`;
